// "Teams contacts" next to "Kjuup contacts" in the new-chat pickers (MemberSelection,
// AddProjectChatUser): the tabs, the Microsoft account chips, the Teams people list and the two
// Teams calls a picker makes (a Teams-only chat, the Teams copy of a new combined group). The
// rules (what a selection creates, the bridge payload) are in utils/teams/combinedChat.js.
import React, { useEffect, useRef, useState } from "react";
import { $crud } from "../../helper/CrudFactory";
import MaterialIcon from "../MaterialIcon";
import { ChatListAvatar } from "../../pages/Feeds/ChatListParts";
import { createTeamsBridge, createTeamsChat, listTeamsAccounts, searchTeamsPeople } from "../../helper/teamsApi";
import { TEAMS_PROJECT_EVENT } from "../../utils/teams/teamsChats";
import { teamsAccountLabel, teamsWritableAccounts } from "../../utils/teams/teamsSettingsModel";
import { buildBridgeRequest, isTeamsPersonSelected, teamsChatDisplayName, teamsPersonName } from "../../utils/teams/combinedChat";
import teamsLogo from "../../Icons/Teams.png";
import "./TeamsChat.css";

const SEARCH_DEBOUNCE_MS = 250;

/** Text of a language key, else the German / English fallback. */
export const teamsPickerText = (strings, isGerm, key, de, en) => (strings && strings[key]) || (isGerm ? de : en);

/**
 * The Microsoft accounts a chat can be started with (connected or importing), loaded once while
 * `enabled`. → { accounts (null while loading, [] without one), accountId, setAccountId }.
 */
export function useTeamsAccounts(enabled) {
  const [accounts, setAccounts] = useState(null);
  const [accountId, setAccountId] = useState(null);
  useEffect(() => {
    if (!enabled) {
      setAccounts([]);
      setAccountId(null);
      return undefined;
    }
    let alive = true;
    listTeamsAccounts().then((r) => {
      if (!alive) return;
      const list = r.ok ? teamsWritableAccounts(r.accounts) : [];
      setAccounts(list);
      setAccountId(list.length ? String(list[0].id) : null);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return { accounts, accountId, setAccountId };
}

/**
 * Teams people for `query` (GET /teams/people, debounced): the user's Teams chat partners, with
 * 2+ characters also the organisation directory. Runs while `enabled`, whichever tab is shown, so
 * the one search field searches both lists at once. → { people (null while loading), directory }.
 */
export function useTeamsPeopleSearch({ enabled, query, accountId }) {
  const [people, setPeople] = useState(null);
  const [directory, setDirectory] = useState("skipped");
  const requestRef = useRef(0);
  const q = String(query || "").trim();
  useEffect(() => {
    if (!enabled) {
      setPeople([]);
      return undefined;
    }
    const id = ++requestRef.current;
    const timer = setTimeout(() => {
      searchTeamsPeople(q, { accountId }).then((r) => {
        if (id !== requestRef.current) return;
        setPeople(r.people);
        setDirectory(r.directory);
      });
    }, q ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [enabled, q, accountId]);
  return { people, directory };
}

/** "Kjuup contacts | Teams contacts" (kj-segmented), each with the number picked there. */
export function ContactSourceTabs({ tab, onTab, kjuupCount = 0, teamsCount = 0, strings, isGerm, className = "" }) {
  const t = (key, de, en) => teamsPickerText(strings, isGerm, key, de, en);
  const label = (text, n) => (n > 0 ? `${text} (${n})` : text);
  return (
    <div className={`kj-segmented kjc-contact-tabs ${className}`.trim()} role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={tab === "kjuup"}
        className={`kj-segment${tab === "kjuup" ? " is-active" : ""}`}
        onClick={() => onTab("kjuup")}
      >
        {label(t("combined_tab_kjuup", "Kjuup-Kontakte", "Kjuup contacts"), kjuupCount)}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={tab === "teams"}
        className={`kj-segment${tab === "teams" ? " is-active" : ""}`}
        onClick={() => onTab("teams")}
      >
        <img src={teamsLogo} alt="" className="kjc-contact-tabs__logo" />
        {label(t("combined_tab_teams", "Teams-Kontakte", "Teams contacts"), teamsCount)}
      </button>
    </div>
  );
}

/**
 * The Teams tab: account chips (several accounts), the people (picked ones the search no longer
 * returns stay on top, see combinedChat.mergeTeamsPeople) and the directory note.
 */
export function TeamsContactsPanel({ accounts, accountId, onPickAccount, people, directory, query, selected, onToggle, disabled, strings, isGerm }) {
  const t = (key, de, en) => teamsPickerText(strings, isGerm, key, de, en);
  const typed = String(query || "").trim().length >= 2;
  const loading = people === null || accounts === null;
  const list = people || [];
  return (
    <div className="kjc-contact-teams">
      {accounts && accounts.length > 1 ? (
        <div className="kjc-teams-new__accounts" role="radiogroup" aria-label={t("teams_new_chat_account", "Konto", "Account")}>
          {accounts.map((a) => {
            const on = String(a.id) === accountId;
            return (
              <button
                type="button"
                key={a.id}
                role="radio"
                aria-checked={on}
                className={`kjc-teams-new__account${on ? " is-active" : ""}`}
                onClick={() => onPickAccount(String(a.id))}
                disabled={disabled}
                title={teamsAccountLabel(a)}
              >
                {teamsAccountLabel(a)}
              </button>
            );
          })}
        </div>
      ) : null}
      <div role="listbox" aria-multiselectable="true">
        {loading ? (
          <div className="kjc-teams-new__empty">{strings.loading || (isGerm ? "Lädt…" : "Loading…")}</div>
        ) : list.length === 0 ? (
          <div className="kjc-teams-new__empty">
            {typed
              ? strings.no_members_found || (isGerm ? "Keine Treffer" : "No results")
              : t("teams_new_chat_type_hint", "Tippe einen Namen, um in deiner Organisation zu suchen.", "Type a name to search your organisation.")}
          </div>
        ) : (
          list.map((p) => {
            const on = isTeamsPersonSelected(selected, p);
            return (
              <button
                type="button"
                key={p.aadUserId}
                role="option"
                aria-selected={on}
                className={`kjc-teams-new__person${on ? " is-selected" : ""}`}
                onClick={() => onToggle(p)}
                disabled={disabled}
              >
                <ChatListAvatar kind="direct" name={teamsPersonName(p)} teams />
                <span className="kjc-teams-new__person-text">
                  <span className="kjc-teams-new__person-name">{teamsPersonName(p)}</span>
                  {p.email || p.jobTitle ? (
                    <span className="kjc-teams-new__person-sub">{[p.jobTitle, p.email].filter(Boolean).join(" · ")}</span>
                  ) : null}
                </span>
                <MaterialIcon icon={on ? "check_circle" : "radio_button_unchecked"} size={20} color={on ? "#5b5fc7" : "#c4c4c4"} />
              </button>
            );
          })
        )}
      </div>
      {typed && directory === "forbidden" ? (
        <div className="kjc-teams-new__note">
          {t(
            "teams_new_chat_directory_forbidden",
            "Deine Organisation erlaubt die Suche im Verzeichnis nicht. Es werden nur Personen aus deinen Teams-Chats gezeigt.",
            "Your organisation does not allow the directory search. Only people from your Teams chats are shown."
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Toast for a Teams person the chat has no room for (TEAMS_MEMBERS_MAX). */
export function notifyTeamsTooMany(strings, isGerm) {
  $crud.notify({
    type: "error",
    message: teamsPickerText(strings, isGerm, "teams_new_chat_too_many", "Zu viele Personen für einen Chat.", "Too many people for one chat."),
  });
}

/**
 * A Teams-only chat (POST /teams/chats): one person = Teams 1:1, more = group chat with the
 * optional topic; buildingId files it under that project. Toasts a failure and returns null, else
 * { groupId, chatType, name, project } for the caller to open (newTeamsChatRow / SET_PENDING_CHAT
 * or the room's Teams chat).
 */
export async function startTeamsOnlyChat({ people, topic = "", buildingId = null, accountId = null, strings, isGerm }) {
  const isGroup = people.length > 1;
  const r = await createTeamsChat({
    members: people.map((p) => p.aadUserId),
    topic: isGroup ? String(topic || "").trim() || null : null,
    buildingId,
    accountId,
  });
  if (!r.ok) {
    const message = r.code === "chat_forbidden"
      ? teamsPickerText(
        strings,
        isGerm,
        "teams_new_chat_forbidden",
        "Microsoft Teams erlaubt diesen Chat nicht (z. B. eine Person außerhalb deiner Organisation oder eine Richtlinie deiner IT).",
        "Microsoft Teams does not allow this chat (e.g. a person outside your organisation or a policy of your IT)."
      )
      : r.message || strings.something_went_wrong;
    $crud.notify({ type: "error", message });
    return null;
  }
  if (r.project) {
    try {
      window.dispatchEvent(new CustomEvent(TEAMS_PROJECT_EVENT, { detail: { groupId: r.groupId, project: r.project } }));
    } catch (_) { /* ignore */ }
  }
  return { groupId: r.groupId, chatType: r.chatType, name: teamsChatDisplayName(people, topic), project: r.project };
}

/**
 * The Teams copy of a combined chat, right after its Kjuup group exists (POST /teams/bridges).
 * Never blocks the group: on failure the caller opens the group anyway and the user gets a toast.
 * Returns the bridge or null (nothing to do without Teams people).
 */
export async function attachTeamsCopy({ groupId, people, accountId = null, strings, isGerm }) {
  if (!groupId || !Array.isArray(people) || people.length === 0) return null;
  const r = await createTeamsBridge(buildBridgeRequest({ groupId, accountId, people }));
  if (r.ok) return r.bridge || {};
  $crud.notify({
    type: "error",
    message: teamsPickerText(strings, isGerm, "combined_bridge_failed", "Die Teams-Kopie konnte nicht erstellt werden.", "Teams copy could not be created."),
  });
  return null;
}
